import request from 'api/request';
import {handleError, formValidation} from 'helpers';

import * as alerts from './alertActions';
import {uploadFile} from '../api/file-upload';
import {myAccountActionTypes as actions} from './actionTypes';
import {
  TWO_FACTOR_FLOW,
  TWO_FACTOR_LOCKOUT_SOURCE,
  TWO_FACTOR_PASSWORD_ERRORS,
  TWO_FACTOR_PASSWORD_LOCKOUT_DEFAULT_SEC,
  TWO_FACTOR_DISABLE_ERRORS,
  TWO_FACTOR_INITIATE_ERRORS,
  TWO_FACTOR_RESEND_ERRORS,
  TWO_FACTOR_RESEND_MAX_ATTEMPTS,
  TWO_FACTOR_RESEND_SUCCESS,
  TWO_FACTOR_SESSION_RESTART_SUFFIX,
  RESEND_COOLDOWN_NORMAL_SEC,
  TWO_FACTOR_VERIFY_ERRORS,
  TWO_FACTOR_VERIFY_LOCKOUT_DEFAULT_SEC,
} from '../constants/twoFactorMessages';
import {
  clearAllPasswordAttemptState,
  incrementPasswordFailedAttempt,
  resetPasswordAttemptsAfterLockoutExpired,
  resetStalePasswordAttemptsAfterExpiredLockout,
  syncPasswordAttemptsOnServerLockout,
} from '../helpers/twoFactorPasswordAttempts';
import {
  clearTwoFactorPasswordLockout,
  getTwoFactorPasswordLockout,
  saveTwoFactorPasswordLockout,
} from '../helpers/twoFactorPasswordLockout';
import {isNetworkConnectionError} from '../helpers/networkError';
import {
  clearAllVerifyAttemptState,
  clearHitFiveAttemptFlag,
  clearResendGuidanceDismissed,
  clearUserVerifyAttemptCounters,
  clearVerifyAttemptLimitActive,
  clearVerifyLockout,
  getHitFiveAttemptFlag,
  getResendCooldownRemaining,
  getResendCount,
  getVerifyLockout,
  incrementFailedAttempt,
  incrementResendCount,
  isResendBlocked,
  isVerifyAttemptLimitActive,
  isVerifyLockedOut,
  resetVerifyAttempts,
  resetVerifyAttemptsAfterLockoutExpired,
  resetStaleVerifyStateAfterExpiredLockout,
  resetVerifyStateAfterResend,
  saveResendCooldownFromSeconds,
  saveVerifyLockout,
  setResendBlocked,
  setResendCountToMax,
  startResendCooldown,
} from '../helpers/twoFactorVerifyAttempts';

const pickApiField = (data, camelKey, snakeKey, defaultValue) => {
  if (data == null) {
    return defaultValue;
  }

  if (data[camelKey] != null) {
    return data[camelKey];
  }

  if (data[snakeKey] != null) {
    return data[snakeKey];
  }

  return defaultValue;
};

export const loadAccount = (options = {}) => dispatch => {
  const silent = Boolean(options.silent);

  if (!silent) {
    dispatch({type: actions.ACCOUNT_LOADING, field: 'account', loading: true});
  }

  return request({url: '/users/me', includePortal: false})
    .then(response => {
      dispatch({type: actions.ACCOUNT_LOAD_SUCCESS, account: response.data});

      return response.data;
    })
    .catch(error => {
      if (!silent) {
        dispatch({
          type: actions.ACCOUNT_LOAD_ERROR,
          field: 'account',
          loading: false,
        });
        handleError('Unable to load the account', error, dispatch);
      }
    });
};

/** Refreshes account contact info without unmounting My Account sub-routes. */
export const refreshAccountSilently = () => loadAccount({silent: true});

export const loadTwoFactorStatus = () => (dispatch, getState) => {
  const {account, loading} = getState().myAccount;
  const userId = account && account.userId;

  if (!userId) {
    if (loading.account || !account) {
      dispatch({
        type: actions.TWO_FACTOR_STATUS_LOADING,
      });
      return Promise.resolve();
    }

    dispatch({
      type: actions.TWO_FACTOR_STATUS_ERROR,
      error: 'Unable to load two-factor authentication settings.',
    });
    return Promise.resolve();
  }

  dispatch({
    type: actions.TWO_FACTOR_STATUS_LOADING,
  });

  return request({
    url: `/Users/${userId}/2fa/status`,
    method: 'GET',
    includePortal: false,
    apiVersion: '1.0',
  })
    .then(response => {
      const data = response.data || {};

      dispatch({
        type: actions.TWO_FACTOR_STATUS_SUCCESS,
        twoFactorStatus: {
          allowDisable:
            data.allowDisable != null
              ? data.allowDisable
              : data.allow_disable != null
                ? data.allow_disable
                : true,
          is2FAEnabled: Boolean(data.is2FAEnabled),
          phoneAvailable:
            data.phoneAvailable != null ? data.phoneAvailable : true,
          twoFactorMethod:
            data.twoFactorMethod != null ? data.twoFactorMethod : null,
        },
      });

      return data;
    })
    .catch(error => {
      const message =
        error && error.message
          ? error.message.replace(/^Unauthorized:\s*/i, '')
          : 'Unable to load two-factor authentication settings.';

      dispatch({
        type: actions.TWO_FACTOR_STATUS_ERROR,
        error: message,
      });
    });
};

/** Alias for Task 2+ to refetch status after enable success modal dismiss. */
export const refreshTwoFactorStatus = loadTwoFactorStatus;

const mapDisableError = (status, data) => {
  const errorCode =
    data && (data.error || data.Error) ? data.error || data.Error : null;
  const apiMessage =
    data && (data.message || data.Message) ? data.message || data.Message : null;

  if (status === 401 || errorCode === 'unauthorized') {
    return TWO_FACTOR_DISABLE_ERRORS.UNAUTHORIZED;
  }

  if (status === 403 || errorCode === 'forbidden') {
    return TWO_FACTOR_DISABLE_ERRORS.FORBIDDEN;
  }

  if (apiMessage) {
    return apiMessage;
  }

  return TWO_FACTOR_DISABLE_ERRORS.GENERIC;
};

export const clearTwoFactorDisableState = () => ({
  type: actions.TWO_FACTOR_DISABLE_RESET,
});

export const disableTwoFactor = () => (dispatch, getState) => {
  const userId =
    getState().myAccount.account && getState().myAccount.account.userId;

  if (!userId) {
    return Promise.resolve({success: false});
  }

  dispatch({type: actions.TWO_FACTOR_DISABLE_LOADING});

  return request({
    url: `/Users/${userId}/2fa`,
    method: 'DELETE',
    includePortal: false,
    apiVersion: '1.0',
  })
    .then(response => {
      if (!response) {
        const error = TWO_FACTOR_DISABLE_ERRORS.GENERIC;
        dispatch({type: actions.TWO_FACTOR_DISABLE_ERROR, error});
        return {success: false, error};
      }

      const {status, data} = response;

      if (status === 204 || status === 200) {
        dispatch({type: actions.TWO_FACTOR_DISABLE_SUCCESS});
        return {success: true};
      }

      const error = mapDisableError(status, data);
      dispatch({type: actions.TWO_FACTOR_DISABLE_ERROR, error});
      return {success: false, error};
    })
    .catch(error => {
      const status = error && error.status;
      const message = mapDisableError(status, error && error.data);

      dispatch({
        type: actions.TWO_FACTOR_DISABLE_ERROR,
        error: message,
      });

      return {success: false, error: message};
    });
};

const mapVerifyPasswordError = (status, data, fallbackMessage) => {
  const errorCode =
    data && (data.error || data.Error) ? data.error || data.Error : null;
  const apiMessage =
    data && (data.message || data.Message) ? data.message || data.Message : null;
  const retryAfterSeconds = pickApiField(
    data || {},
    'retryAfterSeconds',
    'retry_after_seconds',
    0,
  );

  if (status === 400 && errorCode === 'password_required') {
    return {
      error: TWO_FACTOR_PASSWORD_ERRORS.REQUIRED,
      lockoutMessage: null,
      retryAfterSeconds: 0,
    };
  }

  if (status === 401 && errorCode === 'invalid_password') {
    return {
      error: apiMessage || TWO_FACTOR_PASSWORD_ERRORS.INVALID,
      lockoutMessage: null,
      retryAfterSeconds: 0,
    };
  }

  if (status === 429 || errorCode === 'rate_limit_exceeded') {
    return {
      error: null,
      lockoutMessage: apiMessage || TWO_FACTOR_PASSWORD_ERRORS.LOCKOUT,
      retryAfterSeconds: retryAfterSeconds || TWO_FACTOR_PASSWORD_LOCKOUT_DEFAULT_SEC,
    };
  }

  if (status === 403 || errorCode === 'forbidden') {
    return {
      error: apiMessage || TWO_FACTOR_PASSWORD_ERRORS.FORBIDDEN,
      lockoutMessage: null,
      retryAfterSeconds: 0,
    };
  }

  return {
    error: fallbackMessage || TWO_FACTOR_PASSWORD_ERRORS.UNKNOWN,
    lockoutMessage: null,
    retryAfterSeconds: 0,
  };
};

export const clearTwoFactorPasswordForm = () => ({
  type: actions.TWO_FACTOR_VERIFY_PASSWORD_CLEAR_FORM,
});

export const clearTwoFactorPasswordLockoutState = () => (dispatch, getState) => {
  const userId =
    getState().myAccount.account && getState().myAccount.account.userId;

  resetPasswordAttemptsAfterLockoutExpired(userId);
  dispatch({type: actions.TWO_FACTOR_VERIFY_PASSWORD_LOCKOUT_CLEARED});
};

export const clearTwoFactorPasswordModalLockout = () => (dispatch, getState) => {
  const lockoutSource =
    getState().myAccount.twoFactorPassword &&
    getState().myAccount.twoFactorPassword.lockoutSource;

  if (lockoutSource === TWO_FACTOR_LOCKOUT_SOURCE.VERIFY) {
    clearTwoFactorVerifyLockoutState()(dispatch, getState);
    dispatch({type: actions.TWO_FACTOR_VERIFY_PASSWORD_LOCKOUT_CLEARED});
    return;
  }

  return clearTwoFactorPasswordLockoutState()(dispatch, getState);
};

export const prepareTwoFactorPasswordModal = flow => (dispatch, getState) => {
  const userId =
    getState().myAccount.account && getState().myAccount.account.userId;

  dispatch(clearTwoFactorPasswordForm());

  resetStalePasswordAttemptsAfterExpiredLockout(userId);
  resetStaleVerifyStateAfterExpiredLockout(userId);

  const passwordLockout = getTwoFactorPasswordLockout(userId);
  if (passwordLockout) {
    dispatch({
      type: actions.TWO_FACTOR_VERIFY_PASSWORD_LOCKOUT_RESTORED,
      lockoutMessage: passwordLockout.message,
      lockoutSource: TWO_FACTOR_LOCKOUT_SOURCE.PASSWORD,
      retryAfterSeconds: passwordLockout.retryAfterSeconds,
    });
    return;
  }

  if (flow === TWO_FACTOR_FLOW.ENABLE) {
    const verifyLockout = getVerifyLockout(userId);
    if (verifyLockout) {
      dispatch({
        type: actions.TWO_FACTOR_VERIFY_PASSWORD_LOCKOUT_RESTORED,
        lockoutMessage: verifyLockout.message,
        lockoutSource: TWO_FACTOR_LOCKOUT_SOURCE.VERIFY,
        retryAfterSeconds: verifyLockout.retryAfterSeconds,
      });
      return;
    }
  }

  dispatch({type: actions.TWO_FACTOR_VERIFY_PASSWORD_LOCKOUT_CLEARED});
};

export const clearTwoFactorPasswordState = () => (dispatch, getState) => {
  const userId =
    getState().myAccount.account && getState().myAccount.account.userId;

  clearAllPasswordAttemptState(userId);
  dispatch({type: actions.TWO_FACTOR_VERIFY_PASSWORD_RESET});
};

export const verifyTwoFactorPassword = (password, flow) => (dispatch, getState) => {
  const userId =
    getState().myAccount.account && getState().myAccount.account.userId;

  if (!userId) {
    return Promise.resolve({success: false});
  }

  resetStalePasswordAttemptsAfterExpiredLockout(userId);
  resetStaleVerifyStateAfterExpiredLockout(userId);

  const passwordLockout = getTwoFactorPasswordLockout(userId);
  if (passwordLockout) {
    dispatch({
      type: actions.TWO_FACTOR_VERIFY_PASSWORD_ERROR,
      error: null,
      lockoutMessage: passwordLockout.message,
      lockoutSource: TWO_FACTOR_LOCKOUT_SOURCE.PASSWORD,
      retryAfterSeconds: passwordLockout.retryAfterSeconds,
    });
    return Promise.resolve({success: false, lockedOut: true});
  }

  if (flow === TWO_FACTOR_FLOW.ENABLE) {
    const verifyLockout = getVerifyLockout(userId);
    if (verifyLockout) {
      dispatch({
        type: actions.TWO_FACTOR_VERIFY_PASSWORD_ERROR,
        error: null,
        lockoutMessage: verifyLockout.message,
        lockoutSource: TWO_FACTOR_LOCKOUT_SOURCE.VERIFY,
        retryAfterSeconds: verifyLockout.retryAfterSeconds,
      });
      return Promise.resolve({success: false, lockedOut: true});
    }
  }

  dispatch({type: actions.TWO_FACTOR_VERIFY_PASSWORD_LOADING});

  return request({
    url: `/Users/${userId}/2fa/verify-password`,
    method: 'POST',
    body: {password},
    includePortal: false,
    apiVersion: '1.0',
  })
    .then(response => {
      if (!response) {
        dispatch({
          type: actions.TWO_FACTOR_VERIFY_PASSWORD_ERROR,
          error: TWO_FACTOR_PASSWORD_ERRORS.UNKNOWN,
          lockoutMessage: null,
          retryAfterSeconds: 0,
        });
        return {success: false};
      }

      const {status, data} = response;

      if (status === 200 && data && (data.verified || data.Verified)) {
        const setupToken = pickApiField(data, 'setupToken', 'setup_token', null);

        clearAllPasswordAttemptState(userId);

        dispatch({
          type: actions.TWO_FACTOR_VERIFY_PASSWORD_SUCCESS,
          setupToken,
        });

        return {success: true, setupToken};
      }

      const mapped = mapVerifyPasswordError(status, data, null);

      if (status === 401 && data && (data.error || data.Error) === 'invalid_password') {
        const attempt = incrementPasswordFailedAttempt(userId);
        if (attempt.lockout) {
          mapped.error = null;
          mapped.lockoutMessage = attempt.lockout.message;
          mapped.retryAfterSeconds = attempt.lockout.retryAfterSeconds;
        }
      }

      if (mapped.lockoutMessage) {
        saveTwoFactorPasswordLockout(
          userId,
          mapped.retryAfterSeconds,
          mapped.lockoutMessage,
        );
        if (status === 429) {
          syncPasswordAttemptsOnServerLockout(userId);
        }
      }

      dispatch({
        type: actions.TWO_FACTOR_VERIFY_PASSWORD_ERROR,
        error: mapped.error,
        lockoutMessage: mapped.lockoutMessage,
        lockoutSource: mapped.lockoutMessage
          ? TWO_FACTOR_LOCKOUT_SOURCE.PASSWORD
          : null,
        retryAfterSeconds: mapped.retryAfterSeconds,
      });

      return {success: false};
    })
    .catch(error => {
      const message = isNetworkConnectionError(error)
        ? TWO_FACTOR_PASSWORD_ERRORS.NETWORK
        : error && error.message
          ? error.message.replace(/^Unauthorized:\s*/i, '')
          : TWO_FACTOR_PASSWORD_ERRORS.UNKNOWN;

      dispatch({
        type: actions.TWO_FACTOR_VERIFY_PASSWORD_ERROR,
        error: message,
        lockoutMessage: null,
        retryAfterSeconds: 0,
      });

      return {success: false};
    });
};

const mapInitiateSetupError = (status, data) => {
  const errorCode =
    data && (data.error || data.Error) ? data.error || data.Error : null;
  const errorDescription =
    data && (data.error_description || data.errorDescription)
      ? data.error_description || data.errorDescription
      : null;

  if (status === 400 && errorCode === '2fa_no_phone') {
    return {
      disableEmail: false,
      disableSms: true,
      error: errorDescription || TWO_FACTOR_INITIATE_ERRORS.NO_PHONE,
    };
  }

  if (status === 400 && errorCode === '2fa_no_email') {
    return {
      disableEmail: true,
      disableSms: false,
      error: errorDescription || TWO_FACTOR_INITIATE_ERRORS.NO_EMAIL,
    };
  }

  if (status === 400 && errorCode === 'invalid_request') {
    return {
      disableEmail: false,
      disableSms: false,
      error: errorDescription || TWO_FACTOR_INITIATE_ERRORS.GENERIC,
    };
  }

  if (status === 429 || errorCode === 'max_retries_exceeded') {
    return {
      disableEmail: false,
      disableSms: false,
      error: errorDescription || TWO_FACTOR_INITIATE_ERRORS.MAX_RETRIES,
    };
  }

  if (status === 502 || errorCode === 'notification_failed') {
    return {
      disableEmail: false,
      disableSms: false,
      error: errorDescription || TWO_FACTOR_INITIATE_ERRORS.NOTIFICATION_FAILED,
    };
  }

  if (status === 503 || errorCode === 'otp_not_configured') {
    return {
      disableEmail: false,
      disableSms: false,
      error: errorDescription || TWO_FACTOR_INITIATE_ERRORS.SERVICE_UNAVAILABLE,
    };
  }

  return {
    disableEmail: false,
    disableSms: false,
    error: errorDescription || TWO_FACTOR_INITIATE_ERRORS.UNKNOWN,
  };
};

export const clearTwoFactorSetupState = () => dispatch => {
  dispatch({type: actions.TWO_FACTOR_SETUP_RESET});
};

export const clearTwoFactorEnableFlowState = () => (dispatch, getState) => {
  const userId =
    getState().myAccount.account && getState().myAccount.account.userId;

  clearAllVerifyAttemptState(userId);
  dispatch({type: actions.TWO_FACTOR_SETUP_RESET});
};

const buildSessionErrorBanner = (data, fallback) => {
  const rawDescription =
    pickApiField(data, 'errorDescription', 'error_description', null) ||
    fallback;
  const description = rawDescription.replace(/\.\s*$/, '');

  return `${description}. ${TWO_FACTOR_SESSION_RESTART_SUFFIX}`;
};

const mapSessionSetupError = (data, fallback) => ({
  bannerError: buildSessionErrorBanner(data, fallback),
  restartOffer: true,
});

const mapVerifySetupError = (status, data) => {
  const errorCode =
    data && (data.error || data.Error) ? data.error || data.Error : null;
  const retryAfterSeconds = pickApiField(
    data || {},
    'retryAfterSeconds',
    'retry_after_seconds',
    TWO_FACTOR_VERIFY_LOCKOUT_DEFAULT_SEC,
  );

  if (errorCode === 'session_expired') {
    return {
      ...mapSessionSetupError(data, TWO_FACTOR_RESEND_ERRORS.SESSION_EXPIRED),
      clearCode: true,
      codeError: null,
      verifyLockoutMessage: null,
      verifyRetryAfterSeconds: 0,
    };
  }

  if (errorCode === 'session_not_found' || status === 404) {
    return {
      ...mapSessionSetupError(data, TWO_FACTOR_RESEND_ERRORS.SESSION_NOT_FOUND),
      clearCode: true,
      codeError: null,
      verifyLockoutMessage: null,
      verifyRetryAfterSeconds: 0,
    };
  }

  if (errorCode === 'invalid_otp') {
    return {
      bannerError: null,
      clearCode: false,
      codeError: 'invalid',
      verifyLockoutMessage: null,
      verifyRetryAfterSeconds: 0,
    };
  }

  if (status === 429 || errorCode === 'max_retries_exceeded') {
    return {
      bannerError: null,
      clearCode: true,
      codeError: null,
      verifyLockoutMessage: TWO_FACTOR_VERIFY_ERRORS.LOCKOUT,
      verifyRetryAfterSeconds: retryAfterSeconds,
    };
  }

  if (status === 408 || status === 504) {
    return {
      bannerError: TWO_FACTOR_VERIFY_ERRORS.GENERIC_ASYNC,
      clearCode: false,
      codeError: null,
      verifyLockoutMessage: null,
      verifyRetryAfterSeconds: 0,
    };
  }

  if (status >= 500 || status === 502 || status === 503) {
    return {
      bannerError: TWO_FACTOR_VERIFY_ERRORS.GENERIC_ASYNC,
      clearCode: false,
      codeError: null,
      verifyLockoutMessage: null,
      verifyRetryAfterSeconds: 0,
    };
  }

  return {
    bannerError: TWO_FACTOR_VERIFY_ERRORS.GENERIC_ASYNC,
    clearCode: false,
    codeError: null,
    verifyLockoutMessage: null,
    verifyRetryAfterSeconds: 0,
  };
};

const dispatchVerifySetupError = (dispatch, payload) => {
  dispatch({
    bannerError: payload.bannerError,
    codeError: payload.codeError,
    hitFiveAttemptFlag: payload.hitFiveAttemptFlag,
    sessionRestartRequired: payload.sessionRestartRequired || false,
    type: actions.TWO_FACTOR_VERIFY_SETUP_ERROR,
    verifyAttemptLimitActive: payload.verifyAttemptLimitActive,
    verifyLockoutMessage: payload.verifyLockoutMessage,
    verifyRetryAfterSeconds: payload.verifyRetryAfterSeconds,
  });
};

const dispatchResendError = (dispatch, payload) => {
  dispatch({
    bannerError: payload.bannerError || null,
    codeError: payload.codeError || null,
    resendBlocked: payload.resendBlocked || false,
    resendCooldownNonce: payload.resendCooldownSeconds
      ? Date.now()
      : undefined,
    resendCooldownSeconds: payload.resendCooldownSeconds || 0,
    resendErrorMessage: payload.resendErrorMessage || null,
    resendSessionError: null,
    sessionRestartRequired: payload.sessionRestartRequired || false,
    type: actions.TWO_FACTOR_RESEND_ERROR,
  });
};

const mapResendSetupError = (status, data) => {
  const errorCode =
    data && (data.error || data.Error) ? data.error || data.Error : null;
  const retryAfterSeconds = pickApiField(
    data || {},
    'retryAfterSeconds',
    'retry_after_seconds',
    RESEND_COOLDOWN_NORMAL_SEC,
  );

  if (status === 401) {
    return {unauthorized: true};
  }

  if (errorCode === 'max_retries_exceeded') {
    return {
      resendBlocked: true,
      resendErrorMessage: TWO_FACTOR_RESEND_ERRORS.MAX_RETRIES,
    };
  }

  if (errorCode === 'resend_too_early' || status === 429) {
    return {
      cooldownSeconds: retryAfterSeconds,
      resendTooEarly: true,
    };
  }

  if (errorCode === 'session_not_found' || status === 404) {
    return mapSessionSetupError(
      data,
      TWO_FACTOR_RESEND_ERRORS.SESSION_NOT_FOUND,
    );
  }

  if (errorCode === 'session_expired') {
    return mapSessionSetupError(data, TWO_FACTOR_RESEND_ERRORS.SESSION_EXPIRED);
  }

  if (errorCode === 'redis_unavailable' || status === 503) {
    return {
      bannerError: TWO_FACTOR_RESEND_ERRORS.GENERIC_ASYNC,
    };
  }

  if (status >= 500 || status === 502 || status === 504 || status === 408) {
    return {
      bannerError: TWO_FACTOR_RESEND_ERRORS.GENERIC_ASYNC,
    };
  }

  return {
    bannerError: TWO_FACTOR_RESEND_ERRORS.GENERIC_ASYNC,
  };
};

export const clearTwoFactorVerifyLockoutState = () => (dispatch, getState) => {
  const userId =
    getState().myAccount.account && getState().myAccount.account.userId;

  if (!userId) {
    return;
  }

  resetVerifyAttemptsAfterLockoutExpired(userId);
  dispatch({type: actions.TWO_FACTOR_VERIFY_LOCKOUT_CLEARED});
};

export const prepareTwoFactorVerifyModal = () => (dispatch, getState) => {
  const {account, twoFactorSetup} = getState().myAccount;
  const userId = account && account.userId;
  const sessionToken = twoFactorSetup && twoFactorSetup.sessionToken;

  if (!userId || !sessionToken) {
    return;
  }

  resetStaleVerifyStateAfterExpiredLockout(userId);

  const verifyLimitActive = isVerifyAttemptLimitActive(userId);
  const lockout = getVerifyLockout(userId);

  if (!verifyLimitActive && !lockout) {
    resetVerifyAttempts(userId, sessionToken);
  }

  const hitFiveAttemptFlag = getHitFiveAttemptFlag(userId);
  const resendBlocked = isResendBlocked(userId);
  let resendCooldownSeconds = getResendCooldownRemaining(userId);

  if (resendCooldownSeconds <= 0 && !resendBlocked) {
    resendCooldownSeconds = startResendCooldown(userId);
  }

  dispatch({
    hitFiveAttemptFlag,
    resendBlocked,
    resendCooldownNonce: resendCooldownSeconds > 0 ? Date.now() : 0,
    resendCooldownSeconds,
    resendErrorMessage: resendBlocked
      ? TWO_FACTOR_RESEND_ERRORS.MAX_RETRIES
      : null,
    type: actions.TWO_FACTOR_VERIFY_MODAL_PREPARED,
    verifyAttemptLimitActive: verifyLimitActive,
    verifyLockoutMessage: verifyLimitActive
      ? TWO_FACTOR_VERIFY_ERRORS.LOCKOUT
      : lockout
        ? lockout.message
        : null,
    verifyRetryAfterSeconds: lockout ? lockout.retryAfterSeconds : 0,
  });
};

export const verifyTwoFactorSetup = otp => (dispatch, getState) => {
  const {account, twoFactorSetup} = getState().myAccount;
  const userId = account && account.userId;
  const sessionToken = twoFactorSetup && twoFactorSetup.sessionToken;
  const method = twoFactorSetup && twoFactorSetup.method;

  if (!userId || !sessionToken || !method) {
    return Promise.resolve({success: false});
  }

  if (isVerifyLockedOut(userId)) {
    const lockout = getVerifyLockout(userId);
    dispatchVerifySetupError(dispatch, {
      bannerError: null,
      codeError: null,
      hitFiveAttemptFlag: getHitFiveAttemptFlag(userId),
      verifyAttemptLimitActive: isVerifyAttemptLimitActive(userId),
      verifyLockoutMessage: isVerifyAttemptLimitActive(userId)
        ? TWO_FACTOR_VERIFY_ERRORS.LOCKOUT
        : lockout
          ? lockout.message
          : TWO_FACTOR_VERIFY_ERRORS.LOCKOUT,
      verifyRetryAfterSeconds: lockout ? lockout.retryAfterSeconds : 0,
    });
    return Promise.resolve({success: false, lockedOut: true, clearCode: true});
  }

  const normalizedOtp = (otp || '').replace(/\D/g, '');

  if (normalizedOtp.length < 6) {
    const attempt = incrementFailedAttempt(userId, sessionToken);
    dispatchVerifySetupError(dispatch, {
      bannerError: null,
      codeError: attempt.lockout ? null : 'invalid',
      hitFiveAttemptFlag: attempt.hitFiveAttemptFlag,
      verifyAttemptLimitActive: Boolean(attempt.lockout),
      verifyLockoutMessage: attempt.lockout ? attempt.lockout.message : null,
      verifyRetryAfterSeconds: attempt.lockout
        ? attempt.lockout.retryAfterSeconds
        : 0,
    });
    return Promise.resolve({
      success: false,
      clearCode: Boolean(attempt.lockout),
      lockedOut: Boolean(attempt.lockout),
    });
  }

  dispatch({type: actions.TWO_FACTOR_VERIFY_SETUP_LOADING});

  return request({
    apiVersion: '1.0',
    body: {
      method,
      otp: normalizedOtp,
      sessionToken,
    },
    includePortal: false,
    method: 'POST',
    url: `/Users/${userId}/2fa/setup/verify`,
  })
    .then(response => {
      if (!response) {
        dispatchVerifySetupError(dispatch, {
          bannerError: TWO_FACTOR_VERIFY_ERRORS.GENERIC_ASYNC,
          codeError: null,
          hitFiveAttemptFlag: getHitFiveAttemptFlag(userId),
          verifyLockoutMessage: null,
          verifyRetryAfterSeconds: 0,
        });
        return {success: false, clearCode: false};
      }

      const {data, status} = response;

      if (status === 204 || status === 200) {
        clearAllVerifyAttemptState(userId);
        dispatch({type: actions.TWO_FACTOR_VERIFY_SETUP_SUCCESS});
        return {success: true};
      }

      const mapped = mapVerifySetupError(status, data);

      if (mapped.codeError === 'invalid') {
        const attempt = incrementFailedAttempt(userId, sessionToken);
        mapped.hitFiveAttemptFlag = attempt.hitFiveAttemptFlag;
        if (attempt.lockout) {
          mapped.codeError = null;
          mapped.clearCode = true;
          mapped.verifyAttemptLimitActive = true;
          mapped.verifyLockoutMessage = attempt.lockout.message;
          mapped.verifyRetryAfterSeconds = attempt.lockout.retryAfterSeconds;
        }
      }

      if (mapped.verifyLockoutMessage && !getVerifyLockout(userId)) {
        saveVerifyLockout(
          userId,
          mapped.verifyRetryAfterSeconds,
          mapped.verifyLockoutMessage,
        );
      }

      dispatchVerifySetupError(dispatch, {
        bannerError: mapped.bannerError,
        codeError: mapped.codeError,
        hitFiveAttemptFlag:
          mapped.hitFiveAttemptFlag != null
            ? mapped.hitFiveAttemptFlag
            : getHitFiveAttemptFlag(userId),
        sessionRestartRequired: Boolean(mapped.restartOffer),
        verifyAttemptLimitActive:
          mapped.verifyAttemptLimitActive != null
            ? mapped.verifyAttemptLimitActive
            : isVerifyAttemptLimitActive(userId),
        verifyLockoutMessage: mapped.verifyLockoutMessage,
        verifyRetryAfterSeconds: mapped.verifyRetryAfterSeconds,
      });

      return {
        success: false,
        clearCode: mapped.clearCode,
        lockedOut: Boolean(mapped.verifyLockoutMessage),
        restartOffer: Boolean(mapped.restartOffer),
      };
    })
    .catch(error => {
      const message =
        error && error.message
          ? error.message.replace(/^Unauthorized:\s*/i, '')
          : null;

      const isTimeout =
        message && message.indexOf('longer than expected') !== -1;

      dispatchVerifySetupError(dispatch, {
        bannerError: isTimeout
          ? TWO_FACTOR_VERIFY_ERRORS.GENERIC_ASYNC
          : message || TWO_FACTOR_VERIFY_ERRORS.GENERIC_ASYNC,
        codeError: null,
        hitFiveAttemptFlag: getHitFiveAttemptFlag(userId),
        verifyLockoutMessage: null,
        verifyRetryAfterSeconds: 0,
      });

      return {success: false, clearCode: false};
    });
};

export const resendTwoFactorCode = () => (dispatch, getState) => {
  const {account, twoFactorSetup} = getState().myAccount;
  const userId = account && account.userId;
  const sessionToken = twoFactorSetup && twoFactorSetup.sessionToken;

  if (!userId || !sessionToken) {
    return Promise.resolve({success: false});
  }

  if (isResendBlocked(userId) || getResendCount(userId) >= TWO_FACTOR_RESEND_MAX_ATTEMPTS) {
    setResendBlocked(userId);
    dispatchResendError(dispatch, {
      resendBlocked: true,
      resendErrorMessage: TWO_FACTOR_RESEND_ERRORS.MAX_RETRIES,
    });
    return Promise.resolve({success: false, resendBlocked: true});
  }

  const cooldownRemaining = getResendCooldownRemaining(userId);
  if (cooldownRemaining > 0) {
    dispatchResendError(dispatch, {
      resendCooldownSeconds: cooldownRemaining,
    });
    return Promise.resolve({success: false, cooldownActive: true});
  }

  dispatch({type: actions.TWO_FACTOR_RESEND_LOADING});

  return request({
    apiVersion: '1.0',
    body: {sessionToken},
    includePortal: false,
    method: 'POST',
    url: `/Users/${userId}/2fa/setup/resend`,
  })
    .then(response => {
      if (!response) {
        dispatchResendError(dispatch, {
          bannerError: TWO_FACTOR_RESEND_ERRORS.GENERIC_ASYNC,
        });
        return {success: false};
      }

      const {data, status} = response;

      if (status === 200 && data) {
        const newSessionToken = pickApiField(
          data,
          'sessionToken',
          'session_token',
          sessionToken,
        );
        incrementResendCount(userId);

        if (getHitFiveAttemptFlag(userId)) {
          resetVerifyStateAfterResend(userId, sessionToken, newSessionToken);
        } else {
          resetVerifyAttempts(userId, sessionToken);
          resetVerifyAttempts(userId, newSessionToken);
        }

        const resendCooldownSeconds = startResendCooldown(userId);

        dispatch({
          expiresIn: pickApiField(data, 'expiresIn', 'expires_in', 300),
          resendBlocked: false,
          resendCooldownNonce: Date.now(),
          resendCooldownSeconds,
          resendErrorMessage: null,
          resendSuccessMessage: TWO_FACTOR_RESEND_SUCCESS,
          sessionToken: newSessionToken,
          type: actions.TWO_FACTOR_RESEND_SUCCESS,
          verifyAttemptLimitActive: false,
          verifyLockoutMessage: null,
          verifyRetryAfterSeconds: 0,
        });

        return {success: true, clearCode: true, resendBlocked: false};
      }

      const mapped = mapResendSetupError(status, data);

      if (mapped.unauthorized) {
        window.location = './../SimpleSignIn.aspx';
        return {success: false};
      }

      if (mapped.resendTooEarly) {
        const resendCooldownSeconds = saveResendCooldownFromSeconds(
          userId,
          mapped.cooldownSeconds,
        );
        dispatchResendError(dispatch, {resendCooldownSeconds});
        return {success: false, cooldownActive: true};
      }

      if (mapped.resendBlocked) {
        setResendBlocked(userId);
        setResendCountToMax(userId);
        dispatchResendError(dispatch, {
          resendBlocked: true,
          resendErrorMessage: mapped.resendErrorMessage,
        });
        return {success: false, resendBlocked: true};
      }

      if (mapped.restartOffer) {
        dispatchResendError(dispatch, {
          bannerError: mapped.bannerError,
          sessionRestartRequired: true,
        });
        return {
          success: false,
          restartOffer: true,
        };
      }

      dispatchResendError(dispatch, {
        bannerError:
          mapped.bannerError || TWO_FACTOR_RESEND_ERRORS.GENERIC_ASYNC,
      });

      return {success: false};
    })
    .catch(error => {
      const message =
        error && error.message
          ? error.message.replace(/^Unauthorized:\s*/i, '')
          : TWO_FACTOR_RESEND_ERRORS.GENERIC_ASYNC;

      if (message && message.indexOf('Unauthorized') !== -1) {
        window.location = './../SimpleSignIn.aspx';
        return {success: false};
      }

      dispatchResendError(dispatch, {
        bannerError: message,
      });

      return {success: false};
    });
};

export const initiateTwoFactorSetup = method => (dispatch, getState) => {
  const userId =
    getState().myAccount.account && getState().myAccount.account.userId;

  if (!userId) {
    return Promise.resolve({success: false});
  }

  dispatch({
    initiatingMethod: method,
    type: actions.TWO_FACTOR_SETUP_INITIATE_LOADING,
  });

  return request({
    apiVersion: '1.0',
    body: {method},
    includePortal: false,
    method: 'POST',
    url: `/Users/${userId}/2fa/setup/initiate`,
  })
    .then(response => {
      if (!response) {
        dispatch({
          disableEmail: false,
          disableSms: false,
          error: TWO_FACTOR_INITIATE_ERRORS.UNKNOWN,
          type: actions.TWO_FACTOR_SETUP_INITIATE_ERROR,
        });
        return {success: false};
      }

      const {data, status} = response;

      if (status === 200 && data) {
        clearResendGuidanceDismissed(userId);

        dispatch({
          expiresIn: pickApiField(data, 'expiresIn', 'expires_in', 300),
          method: pickApiField(
            data,
            'twoFactorMethod',
            'two_factor_method',
            method,
          ),
          sessionToken: pickApiField(
            data,
            'sessionToken',
            'session_token',
            null,
          ),
          type: actions.TWO_FACTOR_SETUP_INITIATE_SUCCESS,
        });

        return {success: true};
      }

      const mapped = mapInitiateSetupError(status, data);

      dispatch({
        disableEmail: mapped.disableEmail,
        disableSms: mapped.disableSms,
        error: mapped.error,
        type: actions.TWO_FACTOR_SETUP_INITIATE_ERROR,
      });

      return {success: false};
    })
    .catch(error => {
      const message =
        error && error.message
          ? error.message.replace(/^Unauthorized:\s*/i, '')
          : TWO_FACTOR_INITIATE_ERRORS.UNKNOWN;

      dispatch({
        disableEmail: false,
        disableSms: false,
        error: message,
        type: actions.TWO_FACTOR_SETUP_INITIATE_ERROR,
      });

      return {success: false};
    });
};

export const loadTimeZone = () => dispatch => {
  dispatch({type: actions.TIMEZONE_LOADING, field: 'timeZone', loading: true});

  return request({url: `/users/me/preferences/tz`, includePortal: false})
    .then(response => {
      dispatch({
        type: actions.TIMEZONE_LOAD_SUCCESS,
        field: 'timeZone',
        loading: false,
      });

      return response.data;
    })
    .catch(error => {
      dispatch({
        type: actions.TIMEZONE_LOAD_FAIL,
        field: 'timeZone',
        loading: false,
      });
      handleError('Unable to load the timezone', error, dispatch);
    });
};

export const loadGoogleUrl = () => dispatch => {
  dispatch({type: actions.GOOGLE_URL_LOADING, field: 'google', loading: true});

  return request({url: '/users/google/authenticate', includePortal: false})
    .then(response => {
      dispatch({
        type: actions.GOOGLE_URL_LOAD_SUCCESS,
        googleUrl: response.data,
      });

      return response.data;
    })
    .catch(error => {
      dispatch({
        type: actions.GOOGLE_URL_LOAD_ERROR,
        field: 'google',
        loading: false,
      });
      handleError('Unable to load the Google url', error, dispatch);
    });
};

export const disconnectGoogle = () => dispatch => {
  dispatch({
    type: actions.GOOGLE_DISCONNECT_LOADING,
    field: 'google',
    loading: true,
  });

  return request({
    url: '/users/google/calendars',
    method: 'DELETE',
    includePortal: false,
  })
    .then(() => {
      dispatch({
        type: actions.GOOGLE_DISCONNECT_LOADING,
        field: 'google',
        loading: false,
      });

      return true;
    })
    .catch(error => {
      dispatch({
        type: actions.GOOGLE_DISCONNECT_ERROR,
        field: 'google',
        loading: false,
      });
      handleError('Unable to disconnect from Google', error, dispatch);
    });
};

export const getGoogleCalendars = () => dispatch => {
  dispatch({type: actions.GOOGLE_CALENDAR_LOADING});

  return request({url: '/users/google/calendars', includePortal: false})
    .then(response => {
      dispatch({
        type: actions.GOOGLE_CALENDAR_LOAD_SUCCESS,
        googleCalendars: response.data.data,
      });

      return response.data.data;
    })
    .catch(error => {
      dispatch({
        type: actions.GOOGLE_CALENDAR_LOAD_FAIL,
        field: 'googleAuthenticate',
        loading: false,
      });
      handleError('Unable to load the Google calendars', error, dispatch);
    });
};

export const authenticateGoogle = (code, state) => dispatch => {
  dispatch({type: actions.GOOGLE_AUTHENTICATE_LOADING});

  return request({
    url: '/users/google/authenticate',
    method: 'POST',
    body: {code, state},
    includePortal: false,
  })
    .then(() => dispatch(getGoogleCalendars()))
    .catch(error => {
      dispatch({
        type: actions.GOOGLE_AUTHENTICATE_FAIL,
        field: 'googleAuthenticate',
        loading: false,
      });
      handleError('Unable to authenticate with Google', error, dispatch);
    });
};

export const selectGoogleCalendar = (id = null, name = null) => dispatch => {
  if (!id) {
    dispatch({
      type: actions.SELECT_GOOGLE_CALENDAR.LOADING,
      field: 'googleNewCalendar',
      loading: true,
    });
  }

  return request({
    url: '/users/google/calendars',
    method: id ? 'PUT' : 'POST',
    body: {id, name},
    includePortal: false,
  })
    .then(() => {
      if (id) {
        dispatch({
          type: actions.SELECT_GOOGLE_CALENDAR.COMPLETE,
          field: 'googleNewCalendar',
          loading: false,
        });
      }

      return true;
    })
    .catch(error => {
      if (id) {
        dispatch({
          type: actions.SELECT_GOOGLE_CALENDAR.COMPLETE,
          field: 'googleNewCalendar',
          loading: false,
        });
      }

      handleError(
        `Unable to ${id ? 'select' : 'create'} Google calendar`,
        error,
        dispatch,
      );
      return false;
    });
};

export const updatePassword = (newPassword, oldPassword) => (
  dispatch,
  getState,
) => {
  dispatch({
    type: actions.UPDATE_PASSWORD_LOADING,
    field: 'password',
    loading: true,
  });

  const {userId} = getState().myAccount.account;

  return request({
    url: `/users/${userId}/password`,
    method: 'POST',
    body: {newPassword, oldPassword},
    includePortal: false,
  })
    .then(() => {
      dispatch(alerts.success('Your password has been updated successfully.'));
      dispatch({
        type: actions.UPDATE_PASSWORD_SUCCESS,
        field: 'password',
        loading: false,
      });

      return true;
    })
    .catch(error => {
      dispatch({
        type: actions.UPDATE_PASSWORD_ERROR,
        field: 'password',
        loading: false,
      });
      handleError('Unable to update your password', error, dispatch);
    });
};

const updateAccountSuccess = dispatch => {
  setTimeout(() => {
    dispatch(alerts.success('Your account has been updated successfully.'));
    dispatch({
      type: actions.UPDATE_ACCOUNT_SUCCESS,
      field: 'account',
      loading: false,
    });
  }, 500);
};

export const updateAccount = account => dispatch => {
  dispatch({
    type: actions.UPDATE_ACCOUNT_LOADING,
    field: 'account',
    loading: true,
  });

  const formObject = formValidation.setupFormBody(account, true);

  if (formObject.hasError) {
    dispatch(alerts.error(formObject.errorMessage));
    dispatch({
      type: actions.UPDATE_ACCOUNT_ERROR,
      field: 'account',
      loading: false,
    });

    return Promise.reject();
  }

  if (formObject.body.length === 0) {
    updateAccountSuccess(dispatch);

    return Promise.resolve();
  }

  let body = formObject.body.filter(x => x.path !== '/timeZoneName');

  return request({
    url: `/users/${account.userId.value}`,
    method: 'PATCH',
    body,
    includePortal: false,
  })
    .then(response => {
      if (!response) {
        throw new Error('no response');
      }

      if (response.status === 422) {
        throw new Error(response.data);
      }

      body = {
        userId: account.userId.value,
        key: 'TZ',
        value: account.timeZoneName.value,
      };

      request({
        url: `/users/${account.userId.value}/preferences/tz`,
        method: 'PUT',
        body,
        includePortal: false,
      })
        .then(result => {
          if (!result) {
            throw new Error('no response');
          }

          if (result.status === 422) {
            throw new Error(result.data);
          }

          updateAccountSuccess(dispatch);
        })
        .catch(error => {
          dispatch({
            type: actions.UPDATE_ACCOUNT_ERROR,
            field: 'account',
            loading: false,
          });
          handleError('Unable to save the account', error, dispatch);
        });
    })
    .catch(error => {
      dispatch({
        type: actions.UPDATE_ACCOUNT_ERROR,
        field: 'account',
        loading: false,
      });
      handleError('Unable to save the account', error, dispatch);
    });
};

export const uploadProfilePicture = (file, userId) => async dispatch => {
  dispatch({
    type: actions.UPLOAD_PROFILE_PICTURE,
    field: 'profilePicture',
    loading: true,
  });

  const response = await uploadFile(`/users/${userId}/image`, file, false);
  if (response && !response.error) {
    dispatch(
      alerts.success('Your profile picture has been updated successfully.'),
    );
    dispatch({
      type: actions.UPLOAD_PROFILE_PICTURE_SUCCESS,
      profilePicture: response.profileImageUri,
    });
  } else {
    dispatch({
      type: actions.UPLOAD_PROFILE_PICTURE_ERROR,
      field: 'profilePicture',
      loading: false,
    });
    handleError('Unable to update profile picture.', null, dispatch);
  }
};

export const removeProfilePicture = userId => dispatch => {
  dispatch({
    type: actions.REMOVE_PROFILE_PICTURE,
    field: 'profilePicture',
    loading: true,
  });

  const body = [
    {
      op: 'replace',
      path: '/PictureFilepath',
      value: '',
    },
  ];

  return request({
    url: `/users/${userId}`,
    method: 'PATCH',
    body,
    includePortal: false,
  }).then(() => {
    dispatch(
      alerts.success('Your profile picture has been removed successfully.'),
    );
    dispatch({type: actions.REMOVE_PROFILE_PICTURE_SUCCESS});
  });
};

export const loadSubscription = () => dispatch => {
  dispatch({
    type: actions.SUBSCRIPTION_LOADING,
    field: 'subscription',
    loading: true,
  });

  return request({url: '/subscription'})
    .then(response => {
      dispatch({
        type: actions.SUBSCRIPTION_LOAD_SUCCESS,
        subscription: response.data,
      });
    })
    .catch(error => {
      dispatch({
        type: actions.SUBSCRIPTION_LOAD_ERROR,
        field: 'subscription',
        loading: false,
      });
      handleError('Unable to load the subscription', error, dispatch);
    });
};



// WEBPACK FOOTER //
// ./src/actions/myAccountActions.js